import { useState, useCallback, useEffect, useMemo } from 'react';
import {
  ListTodo,
  Plus,
  Clock,
  AlertCircle,
  CheckCircle2,
  ListTree,
  List,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { TreeTable } from '@/components/planning/TreeTable';
import { FlatTable } from '@/components/planning/FlatTable';
import { FilterBar } from '@/components/planning/FilterBar';
import { TaskDetailDrawer } from '@/components/planning/TaskDetailDrawer';
import { CreateTaskModal } from '@/components/planning/CreateTaskModal';
import { InlineTaskInput } from '@/components/planning/InlineTaskInput';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { EmptyState } from '@/components/shared/EmptyState';
import { LoadingSpinner } from '@/components/shared/LoadingSpinner';
import { useGetTasksQuery, useUpdateTaskMutation, useDeleteTaskMutation } from '@/store/api/apiSlice';
import { useTaskTree } from '@/hooks/useTaskTree';
import { wsClient } from '@/lib/ws';
import { PlanningEventType, PLANNING_UPDATES_ROOM } from '@cluster/types';
import type { Task, FilterState, TaskStatus } from '@/types';

// ─── Stat Card ───────────────────────────────────────────────────────────────

interface StatCardProps {
  label: string;
  value: number;
  icon: React.ReactNode;
  accent?: string;
}

const accentClasses: Record<string, string> = {
  blue: 'bg-blue-100 dark:bg-blue-900',
  yellow: 'bg-yellow-100 dark:bg-yellow-900',
  green: 'bg-green-100 dark:bg-green-900',
};

function StatCard({ label, value, icon, accent }: StatCardProps) {
  return (
    <Card>
      <CardContent className="flex items-center gap-3 p-4">
        <div
          className={cn(
            'rounded-lg p-2',
            accent ? accentClasses[accent] : 'bg-muted',
          )}
        >
          {icon}
        </div>
        <div>
          <p className="text-2xl font-bold">{value}</p>
          <p className="text-xs text-muted-foreground">{label}</p>
        </div>
      </CardContent>
    </Card>
  );
}

// ─── PlanningPage ────────────────────────────────────────────────────────────

export function PlanningPage() {
  // ── View state ──────────────────────────────────────────────────────────
  const [viewMode, setViewMode] = useState<'nested' | 'flat'>('nested');
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [inlineInputVisible, setInlineInputVisible] = useState(false);
  const [subtaskParentId, setSubtaskParentId] = useState<string | null>(null);
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);

  // ── Filters ─────────────────────────────────────────────────────────────
  const [filters, setFilters] = useState<FilterState>({
    ownerIds: [],
    statuses: [],
    search: '',
    depthScope: 'all',
  });

  // ── Data fetching ───────────────────────────────────────────────────────
  const {
    data: tasksData,
    isLoading,
    isError,
    error,
    refetch,
  } = useGetTasksQuery({ limit: 1000 });

  const tasks = (tasksData as Task[] | undefined) ?? [];

  // ── Mutations ───────────────────────────────────────────────────────────
  const [updateTask] = useUpdateTaskMutation();
  const [deleteTask] = useDeleteTaskMutation();

  // ── Tree hook (filtering + stats) ──────────────────────────────────────
  const { rootTasks, childrenMap, filteredCount, stats } = useTaskTree(
    tasks,
    filters,
  );

  // Derive the full filtered list for FlatTable via DFS (parent before children)
  const filteredTasks = useMemo(() => {
    const result: Task[] = [];
    const visit = (task: Task) => {
      result.push(task);
      const children = childrenMap.get(task._id) ?? [];
      children.forEach(visit);
    };
    rootTasks.forEach(visit);
    return result;
  }, [rootTasks, childrenMap]);

  // ── Build children lookup for cascade logic ────────────────────────────
  const childrenLookup = useMemo(() => {
    const map = new Map<string, Task[]>();
    for (const task of tasks) {
      if (task.parentId) {
        const list = map.get(task.parentId);
        if (list) list.push(task);
        else map.set(task.parentId, [task]);
      }
    }
    return map;
  }, [tasks]);

  // ── Real-time integration via the platform gateway ────────────────────────
  // Planning publishes task events to Redis ('planning:events'); the gateway
  // service fans them out to the Socket.io room PLANNING_UPDATES_ROOM. The
  // authenticated wsClient (JWT) receives them and refetches the task list.
  useEffect(() => {
    const onTaskEvent = () => {
      refetch();
    };

    const unsubConnected = wsClient.on('connected', () => {
      wsClient.subscribe(PLANNING_UPDATES_ROOM).catch(() => {});
    });

    if (!wsClient.connected) {
      wsClient.connect().catch(() => {});
    } else {
      wsClient.subscribe(PLANNING_UPDATES_ROOM).catch(() => {});
    }

    const unsubs = [
      unsubConnected,
      wsClient.on(PlanningEventType.CREATED, onTaskEvent),
      wsClient.on(PlanningEventType.UPDATED, onTaskEvent),
      wsClient.on(PlanningEventType.DELETED, onTaskEvent),
    ];

    return () => {
      unsubs.forEach((unsub) => unsub());
      wsClient.unsubscribe(PLANNING_UPDATES_ROOM).catch(() => {});
    };
  }, [refetch]);

  // ── Cascade: complete all descendants when parent is completed ─────────
  const cascadeCompleteChildren = useCallback(async (parentId: string, status: TaskStatus) => {
    if (status !== 'completed') return;
    const children = childrenLookup.get(parentId) ?? [];
    for (const child of children) {
      if (child.status !== 'completed') {
        updateTask({ id: child._id, body: { status: 'completed' } }).catch(() => {});
      }
      // Recurse
      cascadeCompleteChildren(child._id, status);
    }
  }, [childrenLookup, updateTask]);

  // ── Cascade: complete parent when all children are completed ───────────
  const cascadeCompleteParent = useCallback(async (taskId: string) => {
    const task = tasks.find((t) => t._id === taskId);
    if (!task?.parentId) return;
    const parent = tasks.find((t) => t._id === task.parentId);
    if (!parent || parent.status === 'completed') return;
    const siblings = childrenLookup.get(parent._id) ?? [];
    if (siblings.length > 0 && siblings.every((s) => s.status === 'completed')) {
      updateTask({ id: parent._id, body: { status: 'completed' } }).catch(() => {});
    }
  }, [tasks, childrenLookup, updateTask]);

  // ── Expand / Collapse ──────────────────────────────────────────────────
  const handleToggleExpand = useCallback((id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  // ── Task selection (detail drawer) ─────────────────────────────────────
  const handleSelectTask = useCallback((id: string) => {
    setSelectedTaskId(id);
  }, []);

  const selectedTask = selectedTaskId
    ? tasks.find((t) => t._id === selectedTaskId) ?? null
    : null;

  // ── Subtask creation ───────────────────────────────────────────────────
  const handleAddSubtask = useCallback((parentId: string) => {
    setSubtaskParentId(parentId);
    setCreateModalOpen(true);
  }, []);

  // ── Edit task (opens drawer) ───────────────────────────────────────────
  const handleEditTask = useCallback((id: string) => {
    setSelectedTaskId(id);
  }, []);

  // ── Delete task (confirm dialog) ───────────────────────────────────────
  const handleDeleteRequest = useCallback((id: string) => {
    setDeleteTargetId(id);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteTargetId) return;
    try {
      await deleteTask(deleteTargetId).unwrap();
      if (selectedTaskId === deleteTargetId) setSelectedTaskId(null);
    } catch {
      // error handled by RTK
    }
    setDeleteTargetId(null);
  }, [deleteTargetId, deleteTask, selectedTaskId]);

  // ── Navigate to parent (from drawer) ───────────────────────────────────
  const handleNavigateToParent = useCallback((parentId: string) => {
    setSelectedTaskId(parentId);
    setExpandedIds((prev) => {
      const next = new Set(prev);
      next.add(parentId);
      return next;
    });
  }, []);

  // ── Error message ──────────────────────────────────────────────────────
  const errorMessage = error
    ? `: ${(error as any)?.data?.message || (error as any)?.data?.error?.message || 'Unknown error'}`
    : '';

  const deleteTargetTask = deleteTargetId ? tasks.find((t) => t._id === deleteTargetId) : null;

  // ── Render ──────────────────────────────────────────────────────────────
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-2xl font-bold">Planning</h1>
        <div className="flex flex-wrap items-center gap-2">
          {/* View mode toggle */}
          <div className="flex rounded-md border">
            <Button
              variant={viewMode === 'nested' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('nested')}
            >
              <ListTree className="mr-1 h-4 w-4" /> Nested
            </Button>
            <Button
              variant={viewMode === 'flat' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('flat')}
            >
              <List className="mr-1 h-4 w-4" /> Flat
            </Button>
          </div>
          <Button onClick={() => { setSubtaskParentId(null); setCreateModalOpen(true); }}>
            <Plus className="mr-2 h-4 w-4" /> New Task
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid gap-4 grid-cols-2 md:grid-cols-4">
        <StatCard
          label="Total Tasks"
          value={stats.total}
          icon={<ListTodo className="h-4 w-4 text-muted-foreground" />}
        />
        <StatCard
          label="In Progress"
          value={stats.byStatus.in_progress}
          icon={<Clock className="h-4 w-4 text-blue-600 dark:text-blue-400" />}
          accent="blue"
        />
        <StatCard
          label="Pending"
          value={stats.byStatus.pending}
          icon={<AlertCircle className="h-4 w-4 text-yellow-600 dark:text-yellow-400" />}
          accent="yellow"
        />
        <StatCard
          label="Completed"
          value={stats.byStatus.completed}
          icon={<CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />}
          accent="green"
        />
      </div>

      {/* Filters */}
      <FilterBar filters={filters} onFiltersChange={setFilters} />

      {/* Content */}
      <Card>
        {isLoading ? (
          <LoadingSpinner className="py-12" />
        ) : isError ? (
          <div className="py-8 text-center text-destructive">
            Failed to load tasks{errorMessage}
          </div>
        ) : filteredCount === 0 ? (
          <EmptyState
            title="No tasks found"
            description="Create a task to get started"
            icon={<ListTodo className="h-8 w-8" />}
          />
        ) : viewMode === 'nested' ? (
          <TreeTable
            tasks={filteredTasks}
            expandedIds={expandedIds}
            onToggleExpand={handleToggleExpand}
            onSelectTask={handleSelectTask}
            selectedTaskId={selectedTaskId}
            onEditTask={handleEditTask}
            onAddSubtask={handleAddSubtask}
            onDeleteTask={handleDeleteRequest}
          />
        ) : (
          <FlatTable
            tasks={filteredTasks}
            allTasks={tasks}
            onSelectTask={handleSelectTask}
            selectedTaskId={selectedTaskId}
            onEditTask={handleEditTask}
            onAddSubtask={handleAddSubtask}
            onDeleteTask={handleDeleteRequest}
          />
        )}

        {/* Inline input */}
        <InlineTaskInput
          visible={inlineInputVisible}
          onClose={() => setInlineInputVisible(false)}
          onSuccess={() => refetch()}
        />
      </Card>

      {/* Detail drawer */}
      <TaskDetailDrawer
        task={selectedTask}
        allTasks={tasks}
        open={!!selectedTask}
        onOpenChange={(open) => !open && setSelectedTaskId(null)}
        onAddSubtask={handleAddSubtask}
        onNavigateToParent={handleNavigateToParent}
        onStatusChanged={cascadeCompleteParent}
        onCascadeCompleteChildren={cascadeCompleteChildren}
      />

      {/* Create modal */}
      <CreateTaskModal
        open={createModalOpen}
        onClose={() => {
          setCreateModalOpen(false);
          setSubtaskParentId(null);
        }}
        defaultParentId={subtaskParentId ?? undefined}
        allTasks={tasks}
      />

      {/* Delete confirmation */}
      <ConfirmDialog
        open={!!deleteTargetId}
        onOpenChange={(open) => { if (!open) setDeleteTargetId(null); }}
        title="Delete task"
        description={`Are you sure you want to delete "${deleteTargetTask?.title ?? 'this task'}"? This will also delete all subtasks.`}
        confirmLabel="Delete"
        variant="destructive"
        onConfirm={handleDeleteConfirm}
      />
    </div>
  );
}
